// ThemeProvider — синхронизирует useThemeStore с DOM:
//   - выставляет `data-theme` и класс `.dark` на <html>
//   - слушает prefers-color-scheme при выборе 'system'
//
// `next-themes` НЕ используем (хоть и в deps) — у нас Zustand-based, чтобы не
// плодить два источника правды.
import { type ReactNode, useEffect } from "react";
import { syncCapacitorStatusBar } from "@/lib/sync-status-bar";
import { readAndroidSystemDark, useThemeStore } from "@/stores/theme";

export function ThemeProvider({ children }: { children: ReactNode }) {
	const theme = useThemeStore((s) => s.theme);
	const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
	const setResolvedTheme = useThemeStore((s) => s.setResolvedTheme);

	// Применяем resolvedTheme к <html> + синкаем системный статус-бар (Capacitor):
	// при toggle темы иконки часов/батареи перекрашиваются под фон (иначе на
	// светлой теме остаются белыми и не читаются). На web syncCapacitorStatusBar — noop.
	useEffect(() => {
		const root = document.documentElement;
		root.dataset.theme = resolvedTheme;
		root.classList.toggle("dark", resolvedTheme === "dark");
		root.style.colorScheme = resolvedTheme;
		syncCapacitorStatusBar(resolvedTheme);
	}, [resolvedTheme]);

	// Синхронизируем resolvedTheme с выбором `theme`.
	// КРИТИЧНО для persist: при ре-гидратации store восстанавливает только
	// `theme` (partialize), а `resolvedTheme` остаётся стартовым systemTheme()
	// (часто dark). Без этого эффекта выбор 'light' после reload не применялся
	// — страница возвращалась к тёмной теме. Поэтому для явных light/dark тоже
	// выставляем resolvedTheme, а не только подписываемся на 'system'.
	useEffect(() => {
		if (theme !== "system") {
			setResolvedTheme(theme);
			return;
		}
		// system: резолвим через Android-мост (matchMedia не работает в Capacitor)
		// + fallback matchMedia на web. Слушаем оба источника смены системной темы:
		//   - matchMedia 'change' (web / iOS),
		//   - 'android-system-theme' CustomEvent (MainActivity.onConfigurationChanged).
		const apply = (): void => setResolvedTheme(readAndroidSystemDark() ? "dark" : "light");
		apply();
		const mql = window.matchMedia("(prefers-color-scheme: dark)");
		mql.addEventListener("change", apply);
		window.addEventListener("android-system-theme", apply);
		return () => {
			mql.removeEventListener("change", apply);
			window.removeEventListener("android-system-theme", apply);
		};
	}, [theme, setResolvedTheme]);

	return <>{children}</>;
}
